import type { AdminExercise } from "../api/generated";

interface DeleteExercisePermissionContext {
  isSuperAdmin: boolean;
  isCompanyAdmin: boolean;
  companyId: number | null | undefined;
}

/**
 * Whether the current user may delete this exercise.
 * Mirrors BaseExerciseController.inScope + route split (/system vs /admin):
 * - Super Admin (not impersonating): system exercises only
 * - Company Admin: own-company exercises only (never system)
 */
export function canDeleteExercise(
  exercise: AdminExercise | null | undefined,
  { isSuperAdmin, isCompanyAdmin, companyId }: DeleteExercisePermissionContext
): boolean {
  if (!exercise?.id) return false;

  // Exercises owned by a COMPANY_GROUP_ADMIN (owner-stamped, shared read-only to their companies)
  // may only be deleted by the owning group admin via the owner-scoped /groupAdmin endpoint — never
  // by the companies they are shared to, nor by a system admin. Don't surface a delete affordance.
  if (exercise.ownerUserId != null) return false;

  if (isSuperAdmin) {
    return !!exercise.systemResource;
  }

  if (isCompanyAdmin) {
    if (exercise.systemResource) return false;
    if (exercise.companyId != null) {
      return exercise.companyId === companyId;
    }
    // Legacy company-created rows without company_id — show delete; backend ACL gate decides.
    return true;
  }

  return false;
}
